import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { combineLatest, map, Observable, switchMap, take } from 'rxjs';
import { Store } from '@ngrx/store';

import { ConfigService } from '@fcom/core/services/config/config.service';
import { selectActiveTab } from '@fcom/booking-widget/store/selectors/booking-widget.selector';
import { BookingWidgetAppState } from '@fcom/booking-widget/store/selectors/booking-widget-feature-state.selector';
import { WidgetTab } from '@fcom/booking-widget/interfaces/flight-search.interface';
import { GtmTripType } from '@fcom/common/interfaces/gtm.interface';

import { BookingAppState } from '../interfaces/booking/booking-state.interface';
import { globalBookingLocations } from '../store/selectors/global-booking.selectors';
import { getTripTypeForFlightSearchParams } from '../utils/gtm.utils';

const DD_TRACE_HEADER_TAG_KEY = 'x-dd-flow-type';

export enum FlowType {
  FLIGHT = 'flight',
  AWARD = 'award',
  MULTICITY = 'multicity',
  OPENJAW = 'openjaw',
  UNKNOWN = 'unknown',
}

@Injectable({
  providedIn: 'root',
})
export class DatadogHeadersInterceptor implements HttpInterceptor {
  private configService = inject(ConfigService);
  private store$ = inject<Store<BookingWidgetAppState>>(Store);
  private bookingStore$ = inject<Store<BookingAppState>>(Store);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.url.startsWith(this.configService.cfg.offersUrl) && !req.url.startsWith(this.configService.cfg.cartUrl)) {
      return next.handle(req);
    }

    return this.getFlowType().pipe(
      switchMap((flowType) => {
        const request: HttpRequest<unknown> = req.clone({
          headers: req.headers.set(DD_TRACE_HEADER_TAG_KEY, flowType),
        });
        return next.handle(request);
      })
    );
  }

  private getFlowType(): Observable<FlowType> {
    return combineLatest([this.store$.select(selectActiveTab), this.bookingStore$.pipe(globalBookingLocations())]).pipe(
      take(1),
      map(([widgetTab, flights]) => {
        if (widgetTab === WidgetTab.AWARD) {
          return FlowType.AWARD;
        }

        if (!flights.length) {
          return FlowType.UNKNOWN;
        }

        const tripType = getTripTypeForFlightSearchParams(
          flights.map((flight) => {
            return {
              origin: flight?.origin?.locationCode,
              destination: flight?.destination?.locationCode,
              departureDate: null,
            };
          })
        );

        if (tripType === GtmTripType.OPENJAW) {
          return FlowType.OPENJAW;
        }

        if (tripType === GtmTripType.MULTICITY) {
          return FlowType.MULTICITY;
        }

        return FlowType.FLIGHT;
      })
    );
  }
}
